import React from 'react'
import {
  stepAgentConfig,
  stepAgentConfigPipeline,
  stepInteractContrastUI,
  stepSelectContainerMethod,
  stepSelectLinuxNormalize
} from './common'
import {
  logo_al2023,
  logo_debian,
  logo_docker,
  logo_manual,
  logo_pipeline,
  logo_rpm
} from '../images'
import { ImageIcon } from '../common/image-icon'
import { formatStyles } from '../../../../react-intl-helpers'
import {
  stepContainerClusterAgentAndAgent,
  stepContainerConfigureOperator,
  stepContainerInjectWorkloads as stepContainerInjectWorkloadsPhp,
  stepContainerInstallOperator
} from './common-container'
import {
  stepKubernetesDownloadSetup,
  stepKubernetesInstallDeployHelm,
  stepKubernetesLabelDeployments as stepKubernetesLabelDeploymentsPhp,
  stepSelectKubernetesOperator
} from './common-kubernetes'
import {
  IconBitbucket,
  IconGithub
} from '@contrast-security-inc/design-system-foundations'
import { OPERATING_SYSTEM } from '../common/constants'

const stepKubernetesLabelDeployments = stepKubernetesLabelDeploymentsPhp('PHP')

const stepContainerInjectWorkloads = stepContainerInjectWorkloadsPhp('PHP')

const stepPHPInstallation = (code1, code2) => ({
  titleMsg: 'title_installPHPAgent',
  defaultIsOpen: true,
  Content: {
    message: 'detail_phpInstallation',
    values: { code1, code2 },
    size: 'large'
  }
})

const stepPHPOptionalInstallation = (code1, code2, code3) => ({
  titleMsg: 'title_installPHPAgent',
  defaultIsOpen: true,
  Content: {
    message: 'detail_phpOptionalInstallation',
    values: { code1, code2, code3 },
    size: 'large'
  }
})

const stepConfigurePHP = {
  titleMsg: 'title_configuringPHP',
  Content: {
    message: 'detail_configureAgentPHP',
    values: {
      version: '<php version>'
    }
  }
}

const commonFinalSteps = [stepAgentConfig, stepInteractContrastUI]

const selectMethod = {
  titleMsg: 'title_selectInstallationMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'debian',
        Content: 'Debian',
        icon: <ImageIcon src={logo_debian} alt="Debian" />
      },
      {
        value: 'rpm',
        Content: 'RPM',
        icon: <ImageIcon src={logo_rpm} alt="RPM" />
      },
      {
        value: 'al2023',
        Content: 'AL2023',
        icon: <ImageIcon src={logo_al2023} alt="AL2023" />
      }
    ]
  }
}

const manuallySteps = [
  {
    ...selectMethod,
    branchKey: 'php-manually-method',
    branches: {
      debian: [
        {
          ...stepPHPInstallation(
            `curl \\\

https://pkg.contrastsecurity.com/api/gpg/key/public | apt-key add -

echo "deb https://pkg.contrastsecurity.com/debian-public/ $(sed -rne 's/^VERSION_CODENAME=(.*)$/\\1/p' /etc/*ease) contrast" \\\

| tee /etc/apt/sources.list.d/contrast.list

echo "deb https://pkg.contrastsecurity.com/debian-public/ all contrast" \\\

| tee -a /etc/apt/sources.list.d/contrast.list`,

            'apt-get update && apt-get install contrast-php-agent'
          )
        },
        stepConfigurePHP,
        ...commonFinalSteps
      ],
      rpm: [
        {
          ...stepPHPOptionalInstallation(
            `tee /etc/yum.repos.d/contrast.repo <<-"EOF"

[contrast]
name=Contrast redhat-$releasever repo
baseurl=https://pkg.contrastsecurity.com/rpm-public/redhat-$releasever/
gpgcheck=0
enabled=1
EOF`,
            'yum install contrast-php-agent',
            'dnf install contrast-php-agent'
          )
        },
        stepConfigurePHP,
        ...commonFinalSteps
      ],
      al2023: [
        {
          ...stepPHPOptionalInstallation(
            `tee /etc/yum.repos.d/contrast.repo <<-"EOF"

[contrast]
name=Contrast AL2023 repo
baseurl=https://pkg.contrastsecurity.com/rpm-public/redhat-9/
gpgcheck=0
enabled=1
EOF`,
            'yum install contrast-php-agent',
            'dnf install contrast-php-agent'
          )
        },
        stepConfigurePHP,
        ...commonFinalSteps
      ]
    }
  }
]

const installMethods = {
  titleMsg: 'title_applicationDeploymentMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'manually',
        Content: {
          message: 'title_installManually'
        },
        icon: <ImageIcon src={logo_manual} alt="manually" />
      },
      {
        value: 'container',
        Content: {
          message: 'title_installContainer'
        },
        icon: <ImageIcon src={logo_docker} alt="container" />
      },
      {
        value: 'pipeline',
        Content: {
          message: 'title_installPipeline'
        },
        icon: <ImageIcon src={logo_pipeline} alt="container" />
      }
    ]
  }
}

const dockerSteps = [
  {
    titleMsg: 'title_buildImageWithAgent',
    defaultIsOpen: true,
    Content: {
      message: 'detail_installImageWithAgentPHP',
      values: {
        ...formatStyles.doclink({ path: 'php-middleware.html' }),
        code: `COPY --from=contrast/agent-php:latest /contrast/contrast.so /usr/local/lib/contrast/php/
RUN echo "extension=/usr/local/lib/contrast/php/contrast.so" >> $(php-config --ini-path)/php.ini
`
      }
    }
  },
  ...commonFinalSteps
]

const stepsLinuxContainer = [
  {
    ...stepSelectContainerMethod,
    branchKey: 'php-container-method',
    branches: {
      docker: dockerSteps,
      kubernetes: [
        {
          ...stepSelectKubernetesOperator,
          branchKey: 'php-kubernetes-op',
          branches: {
            helm: [
              stepKubernetesDownloadSetup,
              stepKubernetesLabelDeployments,
              stepKubernetesInstallDeployHelm,
              stepInteractContrastUI
            ],
            manifest: [
              stepContainerInstallOperator,
              stepContainerConfigureOperator,
              stepContainerInjectWorkloads,
              stepContainerClusterAgentAndAgent,
              stepInteractContrastUI
            ]
          }
        }
      ]
    }
  }
]

const stepSelectPipelineMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethodPipeline',
  Content: {
    options: [
      {
        value: 'github',
        Content: 'Github',
        icon: <IconGithub color="black" />
      },
      {
        value: 'bitbucket',
        Content: 'Bitbucket',
        icon: <IconBitbucket color="blue50" />
      }
    ]
  }
}

const githubDebianInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubPHPDebian',
    values: {
      code1: `- name: Install Agent
  shell: bash
  run:  |
    curl https://pkg.contrastsecurity.com/api/gpg/key/public | sudo apt-key add -
    echo "deb https://pkg.contrastsecurity.com/debian-public/ $(sed -rne 's/^VERSION_CODENAME=(.*)$/\\1/p' /etc/*ease) contrast" | sudo tee /etc/apt/sources.list.d/contrast.list
    echo "deb https://pkg.contrastsecurity.com/debian-public/ all contrast" | sudo tee -a /etc/apt/sources.list.d/contrast.list
    sudo apt-get update && sudo apt-get install contrast-php-agent
    echo "extension=/usr/local/lib/contrast/php/contrast.so" >> \`php-config --ini-path\`/php.ini
`,
      code2: `env:
   CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }}
   CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }}
   CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }}
`,
      token: '{{ secrets.ContrastAPIToken }}',
      name: '{{ secrets.ContrastApplicationName }}',
      metadata: '{{ secrets.ContrastApplicationMetadata }}',
      options: '"-javaagent:/contrast.jar"'
    }
  }
}

const githubRpmInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubPHPRpm',
    values: {
      code1: `- name: Install Agent
  shell: bash
  run: |
    tee /etc/yum.repos.d/contrast.repo <<-"EOF"
    [contrast]
    name=Contrast centos-$releasever repo
    baseurl=https://pkg.contrastsecurity.com/rpm-public/centos-$releasever/
    gpgcheck=0
    enabled=1
    EOF
    sudo yum install contrast-php-agent
`,
      code2: `env:
   CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }}
   CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }}
   CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }}
`,
      token: '{{ secrets.ContrastAPIToken }}',
      name: '{{ secrets.ContrastApplicationName }}',
      metadata: '{{ secrets.ContrastApplicationMetadata }}',
      options: '"-javaagent:/contrast.jar"'
    }
  }
}

const bitbucketDebianInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketPHPDebian',
    values: {
      code: `- step:
  script:
    - curl https://pkg.contrastsecurity.com/api/gpg/key/public | sudo apt-key add -
    - echo "deb https://pkg.contrastsecurity.com/debian-public/ \$(sed -rne 's/^VERSION_CODENAME=(.*)\\$/\\1/p' /etc/*ease) contrast" | sudo tee /etc/apt/sources.list.d/contrast.list
    - echo "deb https://pkg.contrastsecurity.com/debian-public/ all contrast" | sudo tee -a /etc/apt/sources.list.d/contrast.list
    - sudo apt-get update && sudo apt-get install contrast-php-agent
`
    }
  }
}

const bitbucketRpmInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketPHPRpm',
    values: {
      code: `- step:
  script:
    - tee /etc/yum.repos.d/contrast.repo <<-"EOF"

      [contrast]
      name=Contrast centos-\$releasever repo
      baseurl=https://pkg.contrastsecurity.com/rpm-public/centos-\$releasever/
      gpgcheck=0
      enabled=1
      EOF
    - sudo yum install contrast-php-agent
`
    }
  }
}

const pipelineSteps = [
  {
    ...stepSelectPipelineMethod,
    branchKey: 'php-pipeline-method',
    branches: {
      github: [
        {
          ...selectMethod,
          branchKey: 'php-pipeline-github-method',
          branches: {
            debian: [
              githubDebianInstallAgent,
              stepAgentConfigPipeline,
              ...commonFinalSteps
            ],
            rpm: [
              githubRpmInstallAgent,
              stepAgentConfigPipeline,
              ...commonFinalSteps
            ]
          }
        }
      ],
      bitbucket: [
        {
          ...selectMethod,
          branchKey: 'php-pipeline-bitbucket-method',
          branches: {
            debian: [
              bitbucketDebianInstallAgent,
              stepAgentConfigPipeline,
              ...commonFinalSteps
            ],
            rpm: [
              bitbucketRpmInstallAgent,
              stepAgentConfigPipeline,
              ...commonFinalSteps
            ]
          }
        }
      ]
    }
  }
]

const branches = {
  manually: manuallySteps,
  container: stepsLinuxContainer,
  pipeline: pipelineSteps
}

export const phpWizardStepsNormalizeAgentDownload = [
  {
    ...stepSelectLinuxNormalize,
    branchKey: OPERATING_SYSTEM.KEY
  },
  {
    ...installMethods,
    branchKey: 'php-method-linux',
    branches
  }
]
